import {
    FC,
    ReactElement,
    useEffect,
} from 'react'
import { useNavigate, useParams } from 'react-router'

import { Button, InfoListItem, TimeTicker } from '@/components'
import { EmployeesList } from '@/compositions'
import { ExchangeHeader } from '@/containers'
import { isAfter } from '@/helpers/date'
import { convertDateToTime } from '@/helpers/DateTimeParser'
import { getUserFullName } from '@/helpers/getUserFullName'
import { getDeviatedTimeShift, shouldShowEndTime } from '@/helpers/tools'
import { trans } from '@/helpers/trans'
import { ExchangeResource, Shift } from '@/models'
import { ShiftStatus } from '@/models/Shift'
import { getExchange, setExchange } from '@/redux/exchanges/exchangesActions'
import { cancelShiftOffer, registerOfferedShift, unregisterOfferedShift } from '@/redux/shift/shiftActions'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'
import { transformLegacyBasicUserToBasicUser } from '@/services/UserService/transformLegacyBasicUserToBasicUser'

import './ExchangeDetailPage.scss'

const getShiftTime = (shift: Shift, showEndTime: boolean): string => {
    if (showEndTime) {
        return `${convertDateToTime(shift.start)} - ${convertDateToTime(shift.end)}`
    }

    return convertDateToTime(shift.start)
}

// Shows the tracked time of a shift that already started
const renderTrackInfo = (shift: Shift): ReactElement | null => {
    const track = shift.tracks[0]

    if (! track) {
        return null
    }

    return (
        <>
            <InfoListItem label={trans('pages.exchanges.clockedTime')}>
                {track.checkOut
                    ? `${convertDateToTime(track.checkIn)} -
                ${convertDateToTime(track.checkOut)}`
                    : <TimeTicker from={track.checkIn} />}
            </InfoListItem>
            <InfoListItem label={trans('pages.exchanges.workedTime')}>
                {track.totalBillableTime}
            </InfoListItem>
            <InfoListItem label={trans('common.break')}>
                {track.pause} {trans('common.minutes')}
            </InfoListItem>
            <InfoListItem label={trans('common.meal')}>
                {track.meal ? trans(`pages.tracks.${track.meal.toLowerCase()}`) : ''}
            </InfoListItem>
            <InfoListItem label={trans('common.satisfaction')}>
                {track.happiness}
            </InfoListItem>
        </>
    )
}

export const ExchangeDetailPage: FC = (): ReactElement | null => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const { exchangeUuid } = useParams()
    const { exchange } = useTypedSelector(state => state.exchangesReducer)
    const { settings } = useTypedSelector(state => state.userReducer)

    useEffect(() => {
        if (exchangeUuid) {
            dispatch(getExchange(exchangeUuid, navigate))
        }

        return () => {
            dispatch(setExchange(null))
        }
    }, [])

    if (! exchange) {
        return null
    }

    const renderActionButton = (text: string, onClick: () => void): ReactElement => (
        <div className="d-flex justify-content-center">
            <Button
                text={text}
                onClick={onClick}
                className="exchange-detail__action-button"
            />
        </div>
    )

    const renderActions = (currentExchange: ExchangeResource): ReactElement | null => {
        if (! isAfter(new Date(currentExchange.shift.start), new Date())) {
            return renderTrackInfo(currentExchange.shift)
        }

        switch (new ShiftStatus(currentExchange.shift).getStatus()) {
            case 'registeredToOffer':
                return renderActionButton(
                    trans('common.unsubscribe'),
                    () => dispatch(unregisterOfferedShift(currentExchange, navigate)),
                )
            case 'offeredByMe':
                return renderActionButton(
                    trans('pages.absences.revokeRequest'),
                    () => dispatch(cancelShiftOffer(currentExchange.shift, navigate)),
                )
            case 'offered':
                return renderActionButton(
                    trans('pages.exchanges.registerExchange'),
                    () => dispatch(registerOfferedShift(currentExchange, navigate)),
                )
            default:
                return null
        }
    }

    const { shift, owner } = exchange
    const department = shift.department?.name
    const group = shift.department?.group?.name
    const coworkers = (shift.plannedUsers || [])
        .filter(user => user.id !== owner?.id)
        .map(transformLegacyBasicUserToBasicUser)
    const showEndTime = shouldShowEndTime(settings, shift.department?.id)
    const deviatedTime = getDeviatedTimeShift(shift, owner?.id, showEndTime)

    return (
        <>
            <ExchangeHeader date={new Date(shift.start)} />

            <ul className="border-top mb-4">
                <InfoListItem label={trans('common.department')}>
                    {`${group ? `${group} - ` : ''}${department}`}
                </InfoListItem>
                {deviatedTime && (
                    <InfoListItem label={trans('pages.shifts.deviatedShiftTime')}>
                        {deviatedTime}
                    </InfoListItem>
                )}
                <InfoListItem label={deviatedTime ? trans('pages.shifts.originalShiftTime') : trans('common.shiftTime')}>
                    {getShiftTime(shift, showEndTime)}
                </InfoListItem>
                <InfoListItem label={trans('pages.exchanges.exchangeRequestBy')}>
                    {getUserFullName(owner)}
                </InfoListItem>
            </ul>
            <EmployeesList employees={coworkers} />
            {renderActions(exchange)}
        </>
    )
}
